// EquipmentInventory.jsx
// Shows equipment across all programs

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBox, FaCheckCircle, FaInbox, FaArrowRight, FaExclamationTriangle
} from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import styles from './EquipmentInventory.module.css';

const EquipmentInventory = ({
  clubId,
  programs,
  loading
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [equipment, setEquipment] = useState({
    total: 0,
    assigned: 0,
    available: 0,
    byCategory: [],
    byProgram: [],
    loading: true
  });

  // Fetch equipment and assignments data for all programs
  useEffect(() => {
    const fetchEquipmentAndAssignments = async () => {
      if (!programs || programs.length === 0 || !user) {
        setEquipment(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        
        // Get auth token
        const token = await user.getIdToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        // Fetch equipment and assignments for all programs in parallel
        const programResults = await Promise.all(
          programs.map(async (program) => {
            try {
              // Fetch equipment
              const equipmentRes = await fetch(`${backendUrl}/api/sports-equipments?programId=${program._id}`, { headers });
              const equipmentData = equipmentRes.ok ? await equipmentRes.json() : { equipments: [] };
              const items = equipmentData.equipments || equipmentData.equipment || [];
              
              // Fetch assignments separately
              const assignmentsRes = await fetch(`${backendUrl}/api/equipment-assignments/program/${program._id}`, { headers });
              let assignments = [];
              if (assignmentsRes.ok) {
                const assignData = await assignmentsRes.json();
                assignments = Array.isArray(assignData) ? assignData : (assignData.assignments || []);
              }
              
              return { program, items, assignments };
            } catch (e) {
              console.error('[EquipmentInventory] Error for', program.name, ':', e);
              return { program, items: [], assignments: [] };
            }
          })
        );

        // Aggregate data from all programs
        const categoryMap = new Map();
        const programMap = new Map();
        let totalItems = 0;
        let activeAssignments = 0;
        let overdueAssignments = 0;

        for (const { program, items, assignments } of programResults) {
          // Count equipment by category
          for (const item of items) {
            totalItems++;
            const categoryName = item.category?.name || item.categoryName || 'Other';
            if (!categoryMap.has(categoryName)) {
              categoryMap.set(categoryName, { total: 0, assigned: 0, icon: item.category?.icon });
            }
            categoryMap.get(categoryName).total++;
          }

          // Count assignments using same logic as MyAssignmentsView
          const now = new Date();
          let programActive = 0;
          let programOverdue = 0;
          let programReturned = 0;

          assignments.forEach(assignment => {
            const status = (assignment.status || '').toLowerCase();
            
            if (status === 'returned') {
              programReturned++;
            } else if (status === 'active' || status === 'assigned') {
              // Check if overdue (has dueDate and dueDate < now)
              if (assignment.dueDate && new Date(assignment.dueDate) < now) {
                programOverdue++;
              } else {
                programActive++;
              }
            }
          });
          
          activeAssignments += programActive;
          overdueAssignments += programOverdue;
          // programReturned is available if needed in future

          // Track by program (only if has equipment OR assignments)
          if (items.length > 0 || assignments.length > 0) {
            programMap.set(program._id, {
              name: program.name,
              total: items.length,
              activeAssignments: programActive,
              overdueAssignments: programOverdue,
              returnedAssignments: programReturned
            });
          }

          // Update category assignment counts
          for (const assignment of assignments.filter(a => a.status === 'active')) {
            const equipmentItem = items.find(i => i._id === assignment.equipmentId || i._id === assignment.equipment?._id);
            if (equipmentItem) {
              const catName = equipmentItem.category?.name || equipmentItem.categoryName || 'Other';
              if (categoryMap.has(catName)) {
                categoryMap.get(catName).assigned++;
              }
            }
          }
        }

        // Convert maps to arrays
        const byCategory = Array.from(categoryMap.entries()).map(([name, data]) => ({
          name,
          icon: data.icon,
          total: data.total,
          assigned: data.assigned,
          utilization: data.total > 0 ? Math.round((data.assigned / data.total) * 100) : 0
        })).sort((a, b) => b.total - a.total);

        const byProgram = Array.from(programMap.entries()).map(([id, data]) => ({
          id,
          ...data
        })).sort((a, b) => b.total - a.total);

        setEquipment({
          total: totalItems,
          activeAssignments,
          overdueAssignments,
          byCategory,
          byProgram,
          loading: false
        });

      } catch (error) {
        console.error('Error fetching equipment:', error);
        setEquipment(prev => ({ ...prev, loading: false }));
      }
    };

    fetchEquipmentAndAssignments();
  }, [programs, user]);

  const handleManageEquipment = (programId) => {
    if (programId) {
      navigate(`/program/${programId}/equipment`);
    } else {
      navigate(`/clubs/${clubId}`);
    }
  };

  const getCategoryIcon = (category, customIcon) => {
    // Use custom icon from data if available
    if (customIcon) return customIcon;
    
    // Fallback to predefined icons
    const icons = {
      'Balls': '⚽',
      'Balls & Pucks': '⚽',
      'Jerseys': '👕',
      'Goals': '🥅',
      'Gloves': '🧤',
      'Cones': '🦺',
      'Bibs': '🎽',
      'Nets': '🥅',
      'Shoes': '👟',
      'Footwear': '👟',
      'Protective Gear': '🛡️',
      'Training': '🏋️',
      'Other': '📦'
    };
    return icons[category] || '📦';
  };

  if (loading || equipment.loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaBox className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Equipment Inventory</h3>
          <span className={styles.subtitle}>Across All Programs</span>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (equipment.total === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaBox className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Equipment Inventory</h3>
          <span className={styles.subtitle}>Across All Programs</span>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🎒</span>
          <p>No equipment added to any programs yet</p>
          <button className={styles.addButton} onClick={() => handleManageEquipment()}>
            Add Equipment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaBox className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Equipment Inventory</h3>
        <span className={styles.subtitle}>Across All Programs</span>
      </div>

      <div className={styles.content}>
        {/* Summary Cards */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryIcon}>📦</span>
            <div className={styles.summaryData}>
              <span className={styles.summaryValue}>{equipment.total}</span>
              <span className={styles.summaryLabel}>Equipment Items</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryIcon}><FaCheckCircle /></span>
            <div className={styles.summaryData}>
              <span className={styles.summaryValue}>{equipment.activeAssignments || 0}</span>
              <span className={styles.summaryLabel}>Active Assignments</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <span className={`${styles.summaryIcon} ${equipment.overdueAssignments > 0 ? styles.warningIcon : ''}`}>
              {equipment.overdueAssignments > 0 ? <FaExclamationTriangle /> : <FaInbox />}
            </span>
            <div className={styles.summaryData}>
              <span className={`${styles.summaryValue} ${equipment.overdueAssignments > 0 ? styles.warningText : ''}`}>
                {equipment.overdueAssignments || 0}
              </span>
              <span className={styles.summaryLabel}>Overdue</span>
            </div>
          </div>
        </div>

        <div className={styles.columnsGrid}>
          {/* By Category */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>By Category</h4>
            <div className={styles.categoryList}>
              {equipment.byCategory.map((cat, index) => (
                <div key={index} className={styles.categoryRow}>
                  <span className={styles.categoryIcon}>{getCategoryIcon(cat.name, cat.icon)}</span>
                  <span className={styles.categoryName}>{cat.name}</span>
                  <div className={styles.utilizationBar}>
                    <div 
                      className={styles.utilizationFill}
                      style={{ width: `${cat.utilization}%` }}
                    ></div>
                  </div>
                  <span className={styles.categoryStats}>
                    {cat.assigned}/{cat.total}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* By Program */}
          <div className={styles.column}>
            <h4 className={styles.columnTitle}>By Program</h4>
            <div className={styles.programList}>
              {equipment.byProgram.length > 0 ? (
                equipment.byProgram.map((prog, index) => (
                  <div 
                    key={index} 
                    className={styles.programRow}
                    onClick={() => handleManageEquipment(prog.id)}
                  >
                    <span className={styles.programName}>⚽ {prog.name}</span>
                    <span className={styles.programStats}>
                      {prog.total} items
                      {prog.overdueAssignments > 0 && (
                        <span className={styles.overdueTag}> • {prog.overdueAssignments} overdue</span>
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <div className={styles.noPrograms}>
                  No equipment assigned to programs
                </div>
              )}
              
              {/* Programs without equipment */}
              {programs.filter(p => !equipment.byProgram.find(ep => ep.id === p._id)).length > 0 && (
                <div className={styles.warningItem}>
                  <FaExclamationTriangle className={styles.warningIcon} />
                  <span>
                    {programs.filter(p => !equipment.byProgram.find(ep => ep.id === p._id)).length} program(s) have no equipment
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={() => handleManageEquipment()}>
          Manage All Equipment <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default EquipmentInventory;

